import { useCallback, useRef, useState, type DragEvent } from 'react'
import { getDroppedFiles } from '@uppy/utils'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationLongMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import { kNoUploadableFilesMessage } from '@/user_files/drive/constants'

type UseChatFileDropzoneOptions = {
  /** When true, drag-enter is ignored and drop is a no-op (e.g. a view-only chat). */
  disabled: boolean
  onFilesDropped: (files: File[]) => void
}

type ChatFileDropzoneHandlers = {
  onDragEnter: (event: DragEvent<HTMLElement>) => void
  onDragLeave: (event: DragEvent<HTMLElement>) => void
  onDragOver: (event: DragEvent<HTMLElement>) => void
  onDrop: (event: DragEvent<HTMLElement>) => void
}

/**
 * Whole-panel drag-and-drop for chat file uploads. Tracks drag-enter/leave with a counter so the
 * overlay doesn't flicker as the drag crosses child element boundaries, and hands dropped files
 * to the caller's upload pipeline via onFilesDropped.
 */
export function useChatFileDropzone({ disabled, onFilesDropped }: UseChatFileDropzoneOptions): {
  isDraggingFilesOver: boolean
  dragHandlers: ChatFileDropzoneHandlers
} {
  const [isDraggingFilesOver, setIsDraggingFilesOver] = useState(false)
  const dragCounterRef = useRef(0)
  const dispatch = useAppDispatch()

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      dragCounterRef.current++
      if (!disabled && event.dataTransfer.types.includes('Files')) setIsDraggingFilesOver(true)
    },
    [disabled]
  )

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setIsDraggingFilesOver(false)
    }
  }, [])

  const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const onDrop = useCallback(
    async (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      dragCounterRef.current = 0
      setIsDraggingFilesOver(false)
      const { dataTransfer } = event
      if (disabled || !dataTransfer.types.includes('Files')) return

      const showDropError = (error: unknown) => {
        console.error('Drop read error:', error)
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'File could not be uploaded.', durationMs: kToastDurationLongMs }))
      }

      try {
        const files = await getDroppedFiles(dataTransfer, { logDropError: showDropError })
        if (files.length === 0) {
          dispatch(openGlobalToast({ type: GlobalToastType.WARNING, message: kNoUploadableFilesMessage, durationMs: kToastDurationMediumMs }))
          return
        }
        onFilesDropped(files)
      } catch (error) {
        showDropError(error)
      }
    },
    [disabled, dispatch, onFilesDropped]
  )

  return { isDraggingFilesOver, dragHandlers: { onDragEnter, onDragLeave, onDragOver, onDrop } }
}
